import React, { useState, useEffect, useCallback, useRef } from 'react';
//import { useConversation } from '@11labs/react';
import { websocketVirtualAgent } from 'components/virtualagentws/websocketVirtualAgent';
import { useAudit } from './AuditContext';
import { FeedbackMessage } from './types';
import { auditService } from './audit-service';
import { Col, Row } from 'react-bootstrap';
import Button from 'components/base/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMicrophone,
  faMicrophoneSlash,
  faPhoneSlash,
  faPhoneVolume,
  faFileAlt,
  faUserTie,
  faEnvelope,
  faCalendarAlt
} from '@fortawesome/free-solid-svg-icons';
import {
  getVirtualAgentWsUrl,
  getAuthenticatedWsUrl
} from 'services/virtualagent.service';

// Interfaces for messages and conversation status
interface ConversationMessage {
  source: 'user' | 'ai';
  message: string;
  timestamp: Date;
}

interface ConversationMode {
  mode: 'speaking' | 'listening';
}

// Props for the widget component
type AuditWidgetProps = {
  clientId: string;
  clientName: string;
  clientCompany: string;
  agentId: string;
  agentName: string;
  onError?: (error: Error) => void;
  onFeedback?: (message: Omit<FeedbackMessage, 'id' | 'timestamp'>) => void;
  firstMessage: string;
  prompt: string;
  voice: string;
  language: string;
};

/**
 * Conversation widget that handles verbal interaction with the virtual agent for document requests.
 */
const AuditWidget = ({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  clientName,
  clientCompany,
  agentId,
  agentName,
  onError,
  onFeedback,
  firstMessage,
  prompt,
  voice,
  language
}: AuditWidgetProps): JSX.Element => {
  // State for permissions and conversation
  const [hasPermission, setHasPermission] = useState<boolean>(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [clientToolsConfig, setClientToolsConfig] = useState({});
  const [isListening, setIsListening] = useState(false);

  // Get full conversation context
  const {
    selectedClient,
    pendingDocuments,
    setPendingDocuments,
    sentDocuments,
    setSentDocuments,
    conversationStage,
    setConversationStage,
    addSystemMessage
  } = useAudit();

  // Reference for messages container (for automatic scrolling)
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Function to scroll to last message
  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      const scrollContainer = messagesContainerRef.current;
      scrollContainer.scrollTop = scrollContainer.scrollHeight;
    }
  };

  // Update clientTools whenever related states change
  useEffect(() => {
    // Create client tools configuration that the agent can use
    setClientToolsConfig({
      // To handle actions requested by the agent
      // eslint-disable-next-line
      requestAction: async (parameters: { requestedAction: string; documentDetails?: any }) => {
        const { requestedAction, documentDetails } = parameters;

        switch (requestedAction) {
          // Action to mark document as sent
          case 'MARKDOCUMENTSENT': {
            if (!documentDetails || !documentDetails.documentId) {
              if (onFeedback) {
                onFeedback({
                  type: 'document',
                  message: 'Datos insuficientes para registrar el envío',
                  success: false
                });
              }
              return JSON.stringify({
                success: false,
                message: 'Se requiere el ID del documento'
              });
            }

            // Try to mark document as sent
            const result = auditService.markDocumentAsSent(
              documentDetails.documentId
            );

            if (result) {
              // Update documents in context
              if (selectedClient) {
                setPendingDocuments(
                  auditService.getPendingDocuments(selectedClient.id)
                );
                setSentDocuments(
                  auditService.getSentDocuments(selectedClient.id)
                );
              }

              if (onFeedback) {
                onFeedback({
                  type: 'document',
                  message: 'Documento registrado como enviado',
                  success: true
                });
              }

              // Add system message
              addSystemMessage('document', 'Documento registrado como enviado');

              return JSON.stringify({
                success: true,
                message: 'Documento registrado como enviado correctamente'
              });
            } else {
              if (onFeedback) {
                onFeedback({
                  type: 'document',
                  message: 'Error al registrar el envío del documento',
                  success: false
                });
              }

              return JSON.stringify({
                success: false,
                message: 'Error al registrar el documento como enviado'
              });
            }
          }

          // Action to set estimated send date
          case 'SETESTIMATEDSENDDATE': {
            if (
              !documentDetails ||
              !documentDetails.documentId ||
              !documentDetails.estimatedDate
            ) {
              if (onFeedback) {
                onFeedback({
                  type: 'document',
                  message: 'Datos insuficientes para registrar fecha estimada',
                  success: false
                });
              }
              return JSON.stringify({
                success: false,
                message: 'Se requiere el ID del documento y la fecha estimada'
              });
            }

            // Register estimated date
            const result = auditService.setEstimatedSendDate(
              documentDetails.documentId,
              documentDetails.estimatedDate
            );

            if (result) {
              // Update documents in context
              if (selectedClient) {
                setPendingDocuments(
                  auditService.getPendingDocuments(selectedClient.id)
                );
              }

              if (onFeedback) {
                onFeedback({
                  type: 'document',
                  message: 'Fecha estimada registrada',
                  details: `El cliente enviará los documentos aproximadamente el ${documentDetails.estimatedDate}`,
                  success: true
                });
              }

              // Add system message
              addSystemMessage(
                'document',
                'Fecha estimada registrada',
                `El cliente enviará los documentos aproximadamente el ${documentDetails.estimatedDate}`
              );

              return JSON.stringify({
                success: true,
                message: 'Fecha estimada registrada correctamente'
              });
            } else {
              if (onFeedback) {
                onFeedback({
                  type: 'document',
                  message: 'Error al registrar la fecha estimada',
                  success: false
                });
              }

              return JSON.stringify({
                success: false,
                message: 'Error al registrar la fecha estimada'
              });
            }
          }

          // Action to transfer to human auditor
          case 'TRANSFERTOAUDITOR': {
            if (onFeedback) {
              onFeedback({
                type: 'transfer',
                message: 'Transferencia a auditor humano solicitada',
                details: 'La llamada será transferida a un auditor disponible',
                success: true
              });
            }

            // Add system message
            addSystemMessage(
              'transfer',
              'Transferencia a auditor humano solicitada',
              'La llamada será transferida a un auditor disponible'
            );

            return JSON.stringify({
              success: true,
              message: 'Transferencia solicitada correctamente'
            });
          }

          // Default action for cases not covered
          default: {
            if (onFeedback) {
              onFeedback({
                type: 'info',
                message: `Acción no disponible: ${requestedAction}`,
                success: false
              });
            }
            return JSON.stringify({
              success: false,
              data: null,
              message: `Acción no disponible: '${requestedAction}'`
            });
          }
        }
      },

      // To request information from the system
      // eslint-disable-next-line
      requestInfo: async (parameters: { requestedInfo: string; filters?: any }) => {
        const { requestedInfo } = parameters;

        switch (requestedInfo) {
          // Request client information
          case 'DATETIME': {
            // Retorna fecha y hora actuales
            const now = new Date();
            const options: Intl.DateTimeFormatOptions = {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: false
            };
            const dateTimeString = now.toLocaleString('en-GB', options);

            addSystemMessage('info', 'Fecha y Hora actual solicitada');

            return JSON.stringify({
              success: true,
              data: dateTimeString,
              message: ''
            });
          }

          case 'CLIENTINFO': {
            if (!selectedClient) {
              return JSON.stringify({
                success: false,
                message: 'No hay cliente seleccionado'
              });
            }

            addSystemMessage('info', 'Información del cliente solicitada');

            return JSON.stringify({
              success: true,
              data: {
                id: selectedClient.id,
                name: selectedClient.name,
                company: selectedClient.company,
                email: selectedClient.email,
                phoneNumber: selectedClient.phoneNumber
              },
              message: ''
            });
          }

          // Request pending documents
          case 'PENDINGDOCUMENTS': {
            if (!selectedClient) {
              return JSON.stringify({
                success: false,
                message: 'No hay cliente seleccionado'
              });
            }

            const docs = pendingDocuments.map(doc => ({
              id: doc.id,
              documentType: doc.documentType,
              description: doc.description,
              deadline: doc.deadline,
              emailTo: doc.emailTo
            }));

            // Update conversation stage
            setConversationStage('document_listing');

            addSystemMessage(
              'info',
              'Documentos pendientes solicitados',
              `${docs.length} documentos pendientes encontrados`
            );

            return JSON.stringify({
              success: true,
              data: docs,
              message: ''
            });
          }

          // Request sent documents
          case 'SENTDOCUMENTS': {
            if (!selectedClient) {
              return JSON.stringify({
                success: false,
                message: 'No hay cliente seleccionado'
              });
            }

            const docs = sentDocuments.map(doc => ({
              id: doc.id,
              documentType: doc.documentType,
              description: doc.description,
              sentDate: doc.sentDate
            }));

            addSystemMessage(
              'info',
              'Documentos enviados solicitados',
              `${docs.length} documentos enviados encontrados`
            );

            return JSON.stringify({
              success: true,
              data: docs,
              message: ''
            });
          }

          // Request email addresses
          case 'EMAILADDRESSES': {
            // Get all unique email addresses from pending documents
            const emailsSet = new Set<string>();
            pendingDocuments.forEach(doc => {
              doc.emailTo.forEach(email => emailsSet.add(email));
            });

            const emails = Array.from(emailsSet);

            addSystemMessage(
              'info',
              'Direcciones de email solicitadas',
              `${emails.length} direcciones encontradas`
            );

            return JSON.stringify({
              success: true,
              data: emails,
              message: ''
            });
          }

          // Default for uncovered information requests
          default: {
            addSystemMessage(
              'info',
              `Información no disponible: ${requestedInfo}`,
              'Tipo de información solicitada no disponible'
            );

            return JSON.stringify({
              success: false,
              data: null,
              message: `Información no disponible: '${requestedInfo}'`
            });
          }
        }
      },

      END_CALL: async (parameters: { reason: string }) => {
        // Añadimos mensaje de feedback
        if (onFeedback) {
          onFeedback({
            type: 'close',
            message: 'Finalización de llamada por el agente',
            details: `Motivo: ${parameters.reason}`,
            success: true
          });
        }

        return JSON.stringify({
          success: true,
          message: `Finalización de llamada`
        });
      }
    });
  }, [
    selectedClient,
    pendingDocuments,
    sentDocuments,
    setPendingDocuments,
    setSentDocuments,
    conversationStage,
    setConversationStage,
    addSystemMessage,
    onFeedback
  ]);

  // Process initial message to adapt to agent instructions
  const processFirstMessage = (
    message: string,
    customerName: string,
    companyName: string
  ): string => {
    const getSalutation = () => {
      const hour = new Date().getHours();
      if (hour < 13) return 'Buenos días';
      if (hour < 20) return 'Buenas tardes';
      return 'Buenas noches';
    };

    return message
      .replace('{{salutationDayTime}}', getSalutation())
      .replace('{{customerName}}', customerName || '')
      .replace('{{companyName}}', companyName || '');
  };

  // Conversation hook configuration
  const conversation = websocketVirtualAgent({
    onConnect: () => {
      console.log('Connected to agent');
      setIsInitialized(true);
      setIsLoading(false);

      // Add system message
      addSystemMessage('info', 'Conexión establecida con el agente');
    },
    onDisconnect: () => {
      console.log('Disconnected from agent');
      if (onFeedback) {
        onFeedback({
          type: 'close',
          message: 'Llamada finalizada',
          success: true
        });
      }
      setIsInitialized(false);
      setIsLoading(false);

      // Add system message
      addSystemMessage('info', 'Llamada finalizada');
    },
    // eslint-disable-next-line
    onError: (message: string, context?: any) => {
      console.error('Conversation error:', message, context);
      setIsLoading(false);
      if (onError) {
        onError(new Error(message));
      }

      // Add system message
      addSystemMessage('error', `Error: ${message}`);
    },
    onMessage: message => {
      // Add message to conversation with timestamp
      setMessages(prev => [
        ...prev,
        {
          source: message.source,
          message: message.message,
          timestamp: new Date()
        }
      ]);

      // Automatic scrolling
      setTimeout(scrollToBottom, 0);
    },
    onModeChange: ({ mode }: ConversationMode) => {
      // console.log('Mode changed:', mode);
      setIsListening(mode === 'listening');
    },
    // Add client tools configuration
    clientTools: clientToolsConfig
  });

  // Request microphone permission
  const requestMicPermission = async (): Promise<boolean> => {
    try {
      await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      setHasPermission(true);
      return true;
    } catch (error) {
      console.error('Microphone permission denied:', error);
      setHasPermission(false);
      if (onError) {
        onError(new Error('Microphone permission denied'));
      }

      // Add system message
      addSystemMessage('error', 'Permiso de micrófono denegado');

      return false;
    }
  };

  // Start conversation
  const startConversation = useCallback(async (): Promise<void> => {
    if (!hasPermission) {
      const permitted = await requestMicPermission();
      if (!permitted) return;
    }

    try {
      setIsLoading(true);

      // Reset conversation stage
      setConversationStage('identification');

      // End previous session if exists
      await conversation.endSession();

      // Obtener URL firmada
      let signedUrl;
      if (import.meta.env.VITE_NODE_ENV === 'production') {
        // En producción, usar la función asíncrona
        signedUrl = await getAuthenticatedWsUrl();
      } else {
        // En desarrollo, usar la URL directa
        signedUrl = getVirtualAgentWsUrl();
      }

      // Clear previous messages
      setMessages([]);

      // Start session with signed URL
      await conversation.startSession({
        signedUrl,
        overrides: {
          agent: {
            prompt: {
              prompt: prompt
            },
            firstMessage: processFirstMessage(
              firstMessage,
              clientName,
              clientCompany
            ),
            language: language,
            tools: [
              {
                name: 'requestAction',
                description: 'Request a specific action to be performed',
                parameters: {
                  type: 'object',
                  properties: {
                    requestedAction: {
                      type: 'string',
                      description:
                        'The action to request (MARKDOCUMENTSENT, SETESTIMATEDSENDDATE, TRANSFERTOAUDITOR)',
                      enum: [
                        'MARKDOCUMENTSENT',
                        'SETESTIMATEDSENDDATE',
                        'TRANSFERTOAUDITOR'
                      ]
                    },
                    documentDetails: {
                      type: 'object',
                      description: 'Details for the document action',
                      properties: {
                        documentId: {
                          type: 'string',
                          description: 'ID of the document to process'
                        },
                        estimatedDate: {
                          type: 'string',
                          description:
                            'Estimated date for document delivery (transform client response to format YYYY-MM-DD)'
                        }
                      }
                    }
                  },
                  required: ['requestedAction']
                }
              },
              {
                name: 'requestInfo',
                description: 'Request specific information from the system',
                parameters: {
                  type: 'object',
                  properties: {
                    requestedInfo: {
                      type: 'string',
                      description:
                        'The type of information to request (DATETIME, CLIENTINFO, PENDINGDOCUMENTS, SENTDOCUMENTS, EMAILADDRESSES)',
                      enum: [
                        'DATETIME',
                        'CLIENTINFO',
                        'PENDINGDOCUMENTS',
                        'SENTDOCUMENTS',
                        'EMAILADDRESSES'
                      ]
                    }
                  },
                  required: ['requestedInfo']
                }
              },
              {
                id: 'END_CALL',
                name: 'END_CALL',
                description: 'Ends current conversation',
                parameters: {
                  type: 'object',
                  properties: {
                    reason: {
                      type: 'string',
                      description: 'The reason for ending the call'
                    }
                  },
                  required: []
                }
              }
            ]
          },
          tts: {
            voiceId: voice
          }
        }
      });

      // Add system message
      addSystemMessage('info', 'Conversación iniciada');
    } catch (error) {
      console.error('Failed to start conversation:', error);
      if (error instanceof Error && onError) {
        onError(error);
      }

      // Add system message
      addSystemMessage(
        'error',
        `Error al iniciar la conversación: ${
          error instanceof Error ? error.message : 'Error desconocido'
        }`
      );

      setIsLoading(false);
    }
  }, [
    agentId,
    conversation,
    hasPermission,
    onError,
    setConversationStage,
    voice,
    prompt,
    firstMessage,
    clientName,
    clientCompany,
    addSystemMessage
  ]);

  // End conversation
  const endConversation = useCallback(async (): Promise<void> => {
    try {
      await conversation.endSession();
    } catch (error) {
      console.error('Failed to end conversation:', error);
      if (error instanceof Error && onError) {
        onError(error);
      }
    }
  }, [conversation, onError]);

  // Effect to initialize scroll when messages mount
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Effect to access microphone when component mounts
  useEffect(() => {
    void requestMicPermission();

    // Cleanup when unmounting
    return () => {
      if (isInitialized) {
        // Use a synchronous version to avoid promises during unmounting
        try {
          conversation.endSession().catch(err => {
            // Only log the error, don't update state
            console.error('Error during cleanup:', err);
          });
        } catch (error) {
          console.error('Failed during cleanup:', error);
        }
      }
    };
  }, []);

  // Render component
  return (
    <div className="d-flex flex-column h-100">
      <Row className="mb-3">
        <Col className="pe-0" style={{ maxWidth: '170px', width: '170px' }}>
          <div
            className={`d-flex ${
              isInitialized ? 'bg-danger' : 'bg-primary'
            } rounded-5 w-100 justify-content-center`}
          >
            {!isInitialized ? (
              <Button
                onClick={() => void startConversation()}
                className="p-2 text-decoration-none text-white"
                variant="link"
                disabled={!hasPermission || isLoading}
              >
                <FontAwesomeIcon icon={faPhoneVolume} className="me-2 fs-8" />
                {isLoading ? 'Conecting...' : 'Start call'}
              </Button>
            ) : (
              <Button
                onClick={() => void endConversation()}
                className="p-2 text-decoration-none text-white"
                variant="link"
              >
                <FontAwesomeIcon icon={faPhoneSlash} className="me-2 fs-8" />
                Finalizar
              </Button>
            )}
          </div>
        </Col>
        <Col className="d-flex justify-content-end">
          <div className="d-flex justify-content-end text-end fs-10 me-2">
            <Row className="text-body-secondary">
              <div className="pt-1">
                Conectado:
                {conversation.status === 'connected' ? (
                  <FontAwesomeIcon
                    icon={faPhoneVolume}
                    className="mx-2 me-0 fs-10 text-success"
                  />
                ) : (
                  <FontAwesomeIcon
                    icon={faPhoneSlash}
                    className="mx-2 me-0 fs-10 text-danger"
                  />
                )}
              </div>
              <div>
                {conversation.status === 'connected' ? (
                  <>
                    Micrófono:
                    {isListening ? (
                      <FontAwesomeIcon
                        icon={faMicrophone}
                        className="mx-2 me-1 fs-10 text-success"
                      />
                    ) : (
                      <FontAwesomeIcon
                        icon={faMicrophoneSlash}
                        className="mx-1 me-0 fs-10 text-danger"
                      />
                    )}
                  </>
                ) : null}
              </div>
            </Row>
          </div>
        </Col>
      </Row>

      <div
        ref={messagesContainerRef}
        className="p-2 border rounded scrollbar bg-body-emphasis fs-9 lh-sm flex-grow-1"
        style={{
          overflowY: 'auto',
          scrollBehavior: 'smooth',
          height: '280px'
        }}
      >
        {messages.length > 0 ? (
          <div className="w-full space-y-2">
            {messages.map((msg, index) => {
              // Determine icon based on message context
              let icon = null;
              if (msg.source === 'ai') {
                const messageText = msg.message.toLowerCase();
                if (
                  messageText.includes('documento') ||
                  messageText.includes('documentos') ||
                  messageText.includes('enviado')
                ) {
                  icon = (
                    <FontAwesomeIcon
                      icon={faFileAlt}
                      className="text-primary me-2"
                    />
                  );
                } else if (
                  messageText.includes('auditor') ||
                  messageText.includes('contable')
                ) {
                  icon = (
                    <FontAwesomeIcon
                      icon={faUserTie}
                      className="text-info me-2"
                    />
                  );
                } else if (
                  messageText.includes('email') ||
                  messageText.includes('correo')
                ) {
                  icon = (
                    <FontAwesomeIcon
                      icon={faEnvelope}
                      className="text-warning me-2"
                    />
                  );
                } else if (
                  messageText.includes('fecha') ||
                  messageText.includes('plazo')
                ) {
                  icon = (
                    <FontAwesomeIcon
                      icon={faCalendarAlt}
                      className="text-success me-2"
                    />
                  );
                }
              }

              return (
                <div
                  key={index}
                  className={`p-2 rounded border mb-2 ${
                    msg.source === 'user'
                      ? 'bg-body mx-10 me-2'
                      : 'bg-body-secondary mx-2 me-10'
                  }`}
                >
                  <small className="text-muted fs-10 d-block mb-1">
                    {msg.timestamp.toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit'
                    })}
                  </small>
                  <strong>
                    {msg.source === 'user' ? 'Cliente' : agentName}:
                  </strong>
                  &nbsp;
                  <span>
                    {icon}
                    {msg.message}
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center text-muted p-5">
            {isLoading ? (
              <p>Connecting with agent...</p>
            ) : (
              <p>Pulse "Iniciar Llamada" para comenzar la conversación</p>
            )}
          </div>
        )}
      </div>

      {!hasPermission && (
        <div className="alert alert-warning mt-3 mb-0 p-2 fs-9">
          <FontAwesomeIcon icon={faMicrophoneSlash} className="me-2" />
          Se requiere permiso de micrófono para la conversación
        </div>
      )}
    </div>
  );
};

export default AuditWidget;
